import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { steganographyAPI, BatchEncodeResponse, BatchDecodeResponse } from '../services/api';

interface BatchTabProps {
  onImageProcessed?: () => void;
  onMessageEncoded?: () => void;
}

// Styled components for NEXUS design
const BatchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
}));

const SectionPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const DropzoneArea = styled(Box)<{ isdragactive: boolean }>(({ isdragactive, theme }) => ({
  border: isdragactive ? '2px dashed #00F0FF' : '2px dashed #6B6A78',
  borderRadius: '5px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isdragactive ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '30px',
    minHeight: '150px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    minHeight: '120px',
  },
  '&:hover': {
    borderColor: '#00F0FF',
    boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
  },
}));

const DropzoneIcon = styled(Box)(({ theme }) => ({
  fontSize: '48px',
  color: '#6B6A78',
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    marginBottom: '10px',
  },
}));

const DropzoneText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const DropzoneSubtext = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
  },
}));

const NexusTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
      boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#6B6A78',
    fontFamily: '"Press Start 2P", cursive',
    fontSize: '10px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00F0FF',
  },
  '& .MuiInputBase-input': {
    color: '#FFFFFF',
  },
}));

const NexusSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
    },
  },
}));

const NexusButton = styled(Button)<{ nexusVariant?: 'primary' | 'secondary' | 'danger' | string }>(({ nexusVariant = 'primary', theme }) => ({
  border: nexusVariant === 'primary' ? '2px solid #00F0FF' : 
           nexusVariant === 'secondary' ? '2px solid #FFD200' : 
           '2px solid #FF007A',
  color: nexusVariant === 'primary' ? '#00F0FF' : 
         nexusVariant === 'secondary' ? '#FFD200' : 
         '#FF007A',
  padding: '12px 25px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: nexusVariant === 'primary' ? '#00F0FF' : 
             nexusVariant === 'secondary' ? '#FFD200' : 
             '#FF007A',
    color: nexusVariant === 'primary' ? '#00F0FF' : 
          nexusVariant === 'secondary' ? '#FFD200' : 
          '#FF007A',
    boxShadow: `0 0 15px ${nexusVariant === 'primary' ? 'rgba(0, 240, 255, 0.5)' : 
                           nexusVariant === 'secondary' ? 'rgba(255, 210, 0, 0.5)' : 
                           'rgba(255, 0, 122, 0.5)'}`,
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: nexusVariant === 'primary' ? '#00F0FF' : 
                   nexusVariant === 'secondary' ? '#FFD200' : 
                   '#FF007A',
    color: '#0B0813',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const StatusMessage = styled(Box)<{ type: 'error' | 'success' }>(({ type, theme }) => ({
  padding: '15px',
  border: `1px solid ${type === 'error' ? '#FF007A' : '#39FF14'}`,
  backgroundColor: type === 'error' ? 'rgba(255, 0, 122, 0.1)' : 'rgba(57, 255, 20, 0.1)',
  borderRadius: '5px',
  color: type === 'error' ? '#FF007A' : '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  marginBottom: '20px',
  boxShadow: type === 'error' ? '0 0 15px rgba(255, 0, 122, 0.2)' : '0 0 15px rgba(57, 255, 20, 0.2)',
}));

const FileChip = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: '5px 10px',
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.1)',
  borderRadius: '3px',
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  margin: '3px',
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '5px',
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(107, 106, 120, 0.3)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#00F0FF',
    boxShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  },
}));

const ResultItem = styled(Box)<{ success: boolean }>(({ success, theme }) => ({
  border: `1px solid ${success ? '#39FF14' : '#FF007A'}`,
  backgroundColor: success ? 'rgba(57, 255, 20, 0.05)' : 'rgba(255, 0, 122, 0.05)',
  padding: '15px',
  borderRadius: '5px',
  marginBottom: '10px',
}));

const ResultHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
}));

const ResultFilename = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  fontWeight: 'bold',
}));

const ResultStatus = styled(Box)<{ success: boolean }>(({ success, theme }) => ({
  padding: '3px 8px',
  borderRadius: '3px',
  backgroundColor: success ? 'rgba(57, 255, 20, 0.2)' : 'rgba(255, 0, 122, 0.2)',
  color: success ? '#39FF14' : '#FF007A',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '9px',
}));

const ResultDetails = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '12px',
  wordBreak: 'break-word',
}));

const ResultsPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
}));

interface BatchResult {
  filename: string;
  success: boolean;
  encoded_image?: string;
  message?: string;
  error?: string;
  metrics?: any;
}

const BatchTab: React.FC<BatchTabProps> = ({ onImageProcessed, onMessageEncoded }) => {
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [files, setFiles] = useState<File[]>([]);
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [bitDepth, setBitDepth] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState<BatchResult[]>([]);
  const [progress, setProgress] = useState(0);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.bmp', '.jpg', '.jpeg']
    },
    maxFiles: 20,
    maxSize: 50 * 1024 * 1024, // 50MB per file
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles);
      setResults([]);
      setError('');
      setSuccess('');
    }
  });

  const handleBatchEncode = async () => {
    // Files validation
    if (files.length === 0) {
      setError('Please select at least one image');
      return;
    }
    
    if (files.length > 20) {
      setError('Maximum 20 images allowed per batch.');
      return;
    }
    
    // Validate each file
    const validImageTypes = ['image/png', 'image/bmp', 'image/jpeg', 'image/jpg'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    
    for (const file of files) {
      if (!validImageTypes.includes(file.type)) {
        setError(`Invalid image type: ${file.name}. Please use PNG, BMP, or JPEG.`);
        return;
      }
      
      if (file.size > maxSize) {
        setError(`Image size exceeds 50MB limit: ${file.name}`);
        return;
      }
    }
    
    // Message validation
    if (!message.trim()) {
      setError('Please enter a message to hide');
      return;
    }
    
    if (message.length > 100000) {
      setError('Message is too long. Maximum 100,000 characters allowed.');
      return;
    }
    
    if (message.length < 1) {
      setError('Message cannot be empty.');
      return;
    }
    
    // Password validation
    if (!password) {
      setError('Please enter a password');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    if (password.length > 128) {
      setError('Password is too long. Maximum 128 characters allowed.');
      return;
    }
    
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter.');
      return;
    }
    
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter.');
      return;
    }
    
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number.');
      return;
    }
    
    // Bit depth validation
    if (![1, 2, 4].includes(bitDepth)) {
      setError('Invalid bit depth. Must be 1, 2, or 4.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response: BatchEncodeResponse = await steganographyAPI.batchEncode(
        files,
        message,
        password,
        bitDepth
      );

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success && response.results) {
        setResults(response.results);
        const successful = response.results.filter(r => r.success).length;
        setSuccess(`Successfully processed ${successful} of ${response.total_processed} images`);
        onImageProcessed?.();
        onMessageEncoded?.();
      } else {
        setError(response.error || 'Batch encoding failed');
      }
    } catch (err: any) {
      setError(err.message || 'Batch encoding failed');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleBatchDecode = async () => {
    // Files validation
    if (files.length === 0) {
      setError('Please select at least one image');
      return;
    }
    
    if (files.length > 20) {
      setError('Maximum 20 images allowed per batch.');
      return;
    }
    
    // Validate each file
    const validImageTypes = ['image/png', 'image/bmp', 'image/jpeg', 'image/jpg'];
    const maxSize = 50 * 1024 * 1024; // 50MB
    
    for (const file of files) {
      if (!validImageTypes.includes(file.type)) {
        setError(`Invalid image type: ${file.name}. Please use PNG, BMP, or JPEG.`);
        return;
      }
      
      if (file.size > maxSize) {
        setError(`Image size exceeds 50MB limit: ${file.name}`);
        return;
      }
    }
    
    // Password validation
    if (!password) {
      setError('Please enter a password');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    
    if (password.length > 128) {
      setError('Password is too long. Maximum 128 characters allowed.');
      return;
    }
    
    // Bit depth validation
    if (![1, 2, 4].includes(bitDepth)) {
      setError('Invalid bit depth. Must be 1, 2, or 4.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response: BatchDecodeResponse = await steganographyAPI.batchDecode(
        files,
        password,
        bitDepth
      );

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        setResults(response.results);
        const successful = response.results.filter(r => r.success).length;
        setSuccess(`Found messages in ${successful} of ${response.total_processed} images`);
        onImageProcessed?.();
      } else {
        setError(response.error || 'Batch decoding failed');
      }
    } catch (err: any) {
      setError(err.message || 'Batch decoding failed');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const handleProcess = () => {
    if (mode === 'encode') {
      handleBatchEncode();
    } else {
      handleBatchDecode();
    }
  };

  const handleDownloadAll = () => {
    results.forEach((result, index) => {
      if (result.success && result.encoded_image) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${result.encoded_image}`;
        link.download = `encoded_${result.filename}`;
        link.click();
      }
    });
  };

  const handleDownloadResults = () => {
    const csvContent = [
      ['Filename', 'Status', mode === 'encode' ? 'PSNR' : 'Message Length', 'Error'],
      ...results.map(result => [
        result.filename,
        result.success ? 'Success' : 'Failed',
        mode === 'encode' && result.metrics?.psnr ? result.metrics.psnr.toFixed(2) : 
        mode === 'decode' && result.message ? result.message.length.toString() : '',
        result.error || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `batch_results_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setFiles([]);
    setMessage('');
    setPassword('');
    setResults([]);
    setError('');
    setSuccess('');
    setProgress(0);
  };

  const getTotalFileSize = () => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  const getSuccessfulCount = () => {
    return results.filter(r => r.success).length;
  };

  const getFailedCount = () => {
    return results.filter(r => !r.success).length;
  };

  return (
    <BatchContainer>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Mode Selection */}
        <Grid item xs={12}>
          <SectionPanel>
            <SectionTitle>OPERATION MODE</SectionTitle>
            <FormControl fullWidth>
              <InputLabel style={{ color: '#6B6A78', fontFamily: '"Press Start 2P", cursive', fontSize: '10px' }}>SELECT MODE</InputLabel>
              <NexusSelect
                value={mode}
                onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
                label="SELECT MODE"
              >
                <MenuItem value="encode">BATCH ENCODE</MenuItem>
                <MenuItem value="decode">BATCH DECODE</MenuItem>
              </NexusSelect>
            </FormControl>
          </SectionPanel>
        </Grid>

        {/* File Upload Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>01 / INPUT FRAGMENTS ({files.length}/20)</SectionTitle>
            
            <DropzoneArea {...getRootProps()} isdragactive={isDragActive}>
              <input {...getInputProps()} />
              <DropzoneIcon>📁</DropzoneIcon>
              <DropzoneText>
                {isDragActive ? 'DROP IMAGES HERE' : 'DRAG & DROP IMAGES OR CLICK TO SELECT'}
              </DropzoneText>
              <DropzoneSubtext>
                PNG, BMP, JPEG (MAX 20 FILES, 50MB EACH)
              </DropzoneSubtext>
            </DropzoneArea>

            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <HelperText>// SELECTED FILES:</HelperText>
                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                  {files.map((file, index) => (
                    <FileChip key={index}>
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </FileChip>
                  ))}
                </Box>
                <HelperText sx={{ mt: 1 }}>
                  // TOTAL: {files.length} FILES ({(getTotalFileSize() / 1024 / 1024).toFixed(2)} MB)
                </HelperText>
              </Box>
            )}
          </SectionPanel>
        </Grid>

        {/* Settings Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>02 / BATCH PARAMETERS</SectionTitle>

            {mode === 'encode' && (
              <>
                <NexusTextField
                  fullWidth
                  multiline
                  rows={3}
                  label="SECRET MESSAGE"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  margin="normal"
                />
                <HelperText>// THIS MESSAGE WILL BE HIDDEN IN ALL IMAGES</HelperText>
              </>
            )}

            <NexusTextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="ENCRYPTION KEY"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <NexusButton
                nexusVariant="secondary"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'HIDE KEY' : 'SHOW KEY'}
              </NexusButton>
            </Box>

            <FormControl fullWidth margin="normal">
              <InputLabel style={{ color: '#6B6A78', fontFamily: '"Press Start 2P", cursive', fontSize: '10px' }}>BIT DEPTH</InputLabel>
              <NexusSelect
                value={bitDepth}
                onChange={(e) => setBitDepth(Number(e.target.value))}
                label="BIT DEPTH"
              >
                <MenuItem value={1}>1-BIT (MOST SECURE)</MenuItem>
                <MenuItem value={2}>2-BIT (BALANCED)</MenuItem>
                <MenuItem value={4}>4-BIT (MAXIMUM CAPACITY)</MenuItem>
              </NexusSelect>
            </FormControl>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <NexusButton
                nexusVariant="primary"
                onClick={handleProcess}
                disabled={loading || files.length === 0 || !password || (mode === 'encode' && !message)}
                fullWidth
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : `📦 BATCH ${mode === 'encode' ? 'ENCODE' : 'DECODE'}`}
              </NexusButton>
              <NexusButton
                nexusVariant="danger"
                onClick={handleClear}
                fullWidth
              >
                CLEAR
              </NexusButton>
            </Box>
          </SectionPanel>
        </Grid>

        {/* Progress */}
        {loading && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ProgressBar variant="determinate" value={progress} sx={{ flex: 1 }} />
              <HelperText>{progress}%</HelperText>
            </Box>
          </Grid>
        )}

        {/* Status Messages */}
        {error && (
          <Grid item xs={12}>
            <StatusMessage type="error">
              // ERROR: {error}
            </StatusMessage>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <StatusMessage type="success">
              // SUCCESS: {success}
            </StatusMessage>
          </Grid>
        )}

        {/* Results */}
        {results.length > 0 && (
          <Grid item xs={12}>
            <ResultsPanel>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <SectionTitle>BATCH RESULTS ({getSuccessfulCount()} SUCCESSFUL, {getFailedCount()} FAILED)</SectionTitle>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {mode === 'encode' && (
                    <Tooltip title="Download all encoded images">
                      <IconButton onClick={handleDownloadAll} sx={{ color: '#00F0FF' }}>
                        📥
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Download results as CSV">
                    <IconButton onClick={handleDownloadResults} sx={{ color: '#00F0FF' }}>
                      📊
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {results.map((result, index) => (
                  <ResultItem key={index} success={result.success}>
                    <ResultHeader>
                      <ResultFilename>{result.filename}</ResultFilename>
                      <ResultStatus success={result.success}>
                        {result.success ? 'SUCCESS' : 'FAILED'}
                      </ResultStatus>
                    </ResultHeader>
                    {result.success ? (
                      <ResultDetails>
                        {mode === 'encode' ? (
                          <>
                            // MESSAGE SUCCESSFULLY ENCODED
                            {result.metrics && (
                              <HelperText sx={{ mt: 1 }}>
                                // PSNR: {result.metrics.psnr.toFixed(2)} DB | QUALITY: {result.metrics.quality_rating}
                              </HelperText>
                            )}
                          </>
                        ) : (
                          <>
                            // DECODED MESSAGE:<br />
                            {result.message}
                          </>
                        )}
                      </ResultDetails>
                    ) : (
                      <ResultDetails sx={{ color: '#FF007A' }}>
                        // ERROR: {result.error}
                      </ResultDetails>
                    )}
                  </ResultItem>
                ))}
              </Box>
            </ResultsPanel>
          </Grid>
        )}
      </Grid>
    </BatchContainer>
  );
};

export default BatchTab;
